<template>
  <KaizenModal
    :show="show"
    title="Give Feedback"
    subtitle="Report an issue or suggest an improvement to Kaizen."
    icon-color="primary"
    :width="480"
    @close="close"
  >
    <template #icon>
      <MaterialFeedbackIcon />
    </template>

    <div class="feedback-modal__body">
      <!-- Category -->
      <div class="feedback-modal__field">
        <HLText
          size="sm"
          weight="semibold"
          html-tag="label"
        >
          Category
        </HLText>
        <div class="feedback-modal__chips">
          <HLTag
            v-for="cat in categories"
            :id="`feedback-cat-${cat}`"
            :key="cat"
            :color="form.category === cat ? 'primary' : 'gray'"
            size="sm"
            round
            interactive
            @click="form.category = cat"
          >
            {{ cat }}
          </HLTag>
        </div>
      </div>

      <!-- Description -->
      <div class="feedback-modal__field">
        <HLText
          size="sm"
          weight="semibold"
          html-tag="label"
        >
          Description
          <span class="text-error-500">*</span>
        </HLText>
        <HLInput
          id="feedback-description"
          v-model="form.description"
          type="textarea"
          placeholder="What did you expect? What went wrong?"
          :rows="4"
        />
      </div>

      <!-- Screenshot upload -->
      <div class="feedback-modal__field">
        <HLText
          size="sm"
          weight="semibold"
          html-tag="label"
        >
          Screenshot
        </HLText>
        <div
          v-if="screenshotPreviewUrl"
          class="feedback-modal__thumb"
        >
          <img
            :src="screenshotPreviewUrl"
            :alt="screenshotName"
            class="feedback-modal__thumb-img"
          >
          <div class="feedback-modal__thumb-overlay">
            <button
              type="button"
              class="feedback-modal__thumb-btn"
              title="Preview"
              @click.stop="openScreenshotPreview"
            >
              <HLIcon :size="16">
                <EyeIcon />
              </HLIcon>
            </button>
            <button
              type="button"
              class="feedback-modal__thumb-btn feedback-modal__thumb-btn--danger"
              title="Remove"
              :disabled="submitting"
              @click.stop="removeScreenshot"
            >
              <HLIcon :size="16">
                <Trash01Icon />
              </HLIcon>
            </button>
          </div>
          <div class="feedback-modal__thumb-meta">
            <span
              class="feedback-modal__thumb-name"
              :title="screenshotName"
            >{{ screenshotName }}</span>
            <span
              v-if="screenshotSize"
              class="feedback-modal__thumb-size"
            >{{ screenshotSize }}</span>
          </div>
        </div>
        <HLUpload
          v-else
          v-model:file-list="uploadFileList"
          :max="1"
          :disabled="submitting"
          accept="image/*"
          type="draggable"
          :show-file-list="false"
          @change="handleFileUpload"
        >
          <template #buttonContent>
            <HLText variant="body-sm">
              Click to upload or drag & drop
            </HLText>
          </template>
        </HLUpload>
      </div>

      <!-- Page URL (auto-filled) -->
      <div class="feedback-modal__page-url">
        <HLText
          size="3xs"
          html-tag="span"
          class="text-secondary-400"
        >
          Page: {{ currentPage }}
        </HLText>
      </div>
    </div>

    <template #footer>
      <HLButton
        variant="secondary"
        size="sm"
        @click="close"
      >
        Cancel
      </HLButton>
      <HLButton
        variant="primary"
        color="primary"
        size="sm"
        :disabled="!form.description.trim()"
        :loading="submitting"
        @click="submit"
      >
        Send Feedback
      </HLButton>
    </template>
  </KaizenModal>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { HLText, HLButton, HLInput, HLUpload, HLTag, HLIcon } from '@platform-ui/highrise'
import { MaterialFeedbackIcon } from '@gohighlevel/ghl-icons/24/material/rounded'
import { EyeIcon, Trash01Icon } from '@gohighlevel/ghl-icons/24/outline'
import KaizenModal from '@/components/ui/KaizenModal.vue'
import { feedbackService } from '@/database/services'
import { useAuthStore } from '@/stores/auth'
import { useToast } from '@/composables/useToast'

const props = defineProps<{
  show: boolean
}>()

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void
}>()

const route = useRoute()
const authStore = useAuthStore()
const { success, error } = useToast()

const categories = ['Bug Report', 'Feature Request', 'UI Issue', 'Performance', 'Other']

const form = reactive({
  category: 'Other',
  description: '',
})

const uploadFileList = ref<any[]>([])
const submitting = ref(false)
const screenshotBlobUrl = ref<string>('')

const currentPage = computed(() => route.fullPath)

const screenshotFile = computed<File | null>(() => {
  const item = uploadFileList.value[0]
  const f = item?.file
  return f instanceof File ? f : null
})

const screenshotPreviewUrl = computed(() => screenshotBlobUrl.value)
const screenshotName = computed(() => uploadFileList.value[0]?.name || screenshotFile.value?.name || '')
const screenshotSize = computed(() => formatBytes(screenshotFile.value?.size))

function formatBytes(bytes?: number): string {
  if (!Number.isFinite(bytes) || !bytes || bytes <= 0) return ''
  const kb = bytes / 1024
  const mb = kb / 1024
  if (mb >= 1) return `${mb.toFixed(2)} MB`
  if (kb >= 1) return `${kb.toFixed(0)} KB`
  return `${bytes} B`
}

function revokeBlobUrl() {
  if (screenshotBlobUrl.value) {
    URL.revokeObjectURL(screenshotBlobUrl.value)
    screenshotBlobUrl.value = ''
  }
}

function handleFileUpload({ fileList }: { fileList: any[] }) {
  // Mark file as finished so HLUpload's internal state stops the 0% progress
  // bar (the component otherwise waits for a real upload action). We render
  // our own thumbnail/preview, so its file list is hidden.
  uploadFileList.value = fileList.map(f => ({
    ...f,
    status: 'finished',
    percentage: 100,
  }))
  revokeBlobUrl()
  if (screenshotFile.value) {
    screenshotBlobUrl.value = URL.createObjectURL(screenshotFile.value)
  }
}

function openScreenshotPreview() {
  if (!screenshotPreviewUrl.value) return
  window.open(screenshotPreviewUrl.value, '_blank', 'noopener')
}

function removeScreenshot() {
  revokeBlobUrl()
  uploadFileList.value = []
}

function resetForm() {
  form.category = 'Other'
  form.description = ''
  removeScreenshot()
}

function close() {
  emit('update:show', false)
}

async function submit() {
  if (!form.description.trim()) return
  submitting.value = true

  try {
    let screenshot_url = ''
    const file = uploadFileList.value[0]?.file

    if (file) {
      screenshot_url = await fileToBase64(file)
    }

    await feedbackService.create({
      category: form.category,
      description: form.description,
      screenshot_url,
      page_url: currentPage.value,
      created_by: authStore.user?.id || '',
      created_by_name: authStore.user?.name || authStore.user?.email || '',
      status: 'open',
    })

    success('Feedback submitted! Thanks for helping improve Kaizen.')
    resetForm()
    close()
  } catch (err) {
    error('Failed to submit feedback. Please try again.')
  } finally {
    submitting.value = false
  }
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

watch(() => props.show, (val) => {
  if (!val) resetForm()
})
</script>

<style scoped>
.feedback-modal__body {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 4px 0;
}

.feedback-modal__field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.feedback-modal__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.feedback-modal__page-url {
  padding: 8px 12px;
  background: var(--neutral-100);
  border-radius: 6px;
}

.feedback-modal__thumb {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 200px;
  max-width: 100%;
  border: 1px solid var(--neutral-200);
  border-radius: 8px;
  overflow: hidden;
  background: var(--neutral-50);
}

.feedback-modal__thumb-img {
  width: 100%;
  height: 120px;
  object-fit: cover;
  display: block;
}

.feedback-modal__thumb-overlay {
  position: absolute;
  inset: 0 0 auto 0;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.45);
  opacity: 0;
  transition: opacity 0.15s ease;
  pointer-events: none;
}

.feedback-modal__thumb:hover .feedback-modal__thumb-overlay {
  opacity: 1;
  pointer-events: auto;
}

.feedback-modal__thumb-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  color: var(--neutral-700);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.feedback-modal__thumb-btn:hover {
  background: #fff;
}

.feedback-modal__thumb-btn--danger:hover {
  background: var(--error-500);
  color: #fff;
}

.feedback-modal__thumb-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.feedback-modal__thumb-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 10px;
  font-size: 12px;
  line-height: 1.3;
  background: var(--base-white);
}

.feedback-modal__thumb-name {
  font-weight: 500;
  color: var(--neutral-700);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.feedback-modal__thumb-size {
  color: var(--neutral-500);
  flex-shrink: 0;
}
</style>
